import { LoggerService } from '@app/shared/services/logger.service';
import { ICertificateData } from '@app/certificates/manage/manage';
import {
  Component, OnInit, OnDestroy, ViewChild, forwardRef, AfterViewInit,
  Input, Output, EventEmitter
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { InfoService } from '@app/core/info/info.service';
import { IMobileDevice, DeviceStatus, statusIcon } from '@app/certificates/devices/devices';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { ConfirmComponent } from '@app/core/info/confirm/confirm.component';
import { IConfirmData } from '@app/core/info/confirm/confirm';
import { Store } from '@ngrx/store';
import {
  selectorDevice,
  ActionDevicesDelete,
  ActionRemoveDeviceFromCertificatе
} from '@app/certificates/devices/devices.reducer';
import { ManageDevicesEffects } from '@app/certificates/devices/devices.effects';
import { IStatusIcon } from '@app/shared/interfaces/common-interfaces';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { environment } from '@env/environment';
import { DevicesService } from '../devices.service';
import { EmergencyService } from '@app/core/auth/component/emergency-login/emergency-login.service';

@Component({
  selector: 'cloud-devices-grid',
  templateUrl: './devices-grid.component.html',
  styleUrls: ['./devices-grid.component.scss', './devices-grid.component-theme.scss']
})
export class DevicesGridComponent implements OnInit, OnDestroy, AfterViewInit {

  private _selectedCert: ICertificateData;

  @Input() isSubcomponent = false;
  @Input() set selectedCert(cert: ICertificateData) {
    this._selectedCert = cert;

    if (cert) {
      const filtereredDevices = this._devices.filter(device => {
        return device.certificateReqIds.find(element => element === cert.certReqId);
      });

      if (filtereredDevices.length !== 0) {
        this.setDeviceData(false, filtereredDevices);
      } else {
        this.setDeviceData(true, []);
      }
    }
  }

  get selectedCert(): ICertificateData {
    return this._selectedCert;
  }

  @Output() onShowCertificates = new EventEmitter<IMobileDevice>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;

  readonly dialogTexts = 'deviceTexts.dialogTexts.';
  readonly resultTexts = 'deviceTexts.resultTexts.';
  readonly displayedColumns = [
    'status',
    'name',
    'registrationDate',
    'lastUpdateDate',
    'certCnt',
    'actions'
  ];

  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;

  readonly icons = iconCodes;
  resultsLength = 0;
  dataSource: MatTableDataSource<IMobileDevice> = new MatTableDataSource();
  _devices: IMobileDevice[] = [];
  unsubscribe$: Subject<void> = new Subject<void>();
  private _confirmDialog: MatDialogRef<ConfirmComponent>;
  isEmergencyLogin: boolean;
  loading = true;
  onEmptyDevices = false;

  constructor(
    private info: InfoService,
    private translateService: TranslateService,
    private emergency: EmergencyService,
    private dialog: MatDialog,
    private store: Store<any>,
    private effects: ManageDevicesEffects,
    private devicesService: DevicesService,
    public tableHelper: MatTableHelper,
    private logger: LoggerService
  ) { }

  /**
   * Тъй като използваме store, а трябва да затворим прозореца, след извършване на операцията,
   * се регистрираме, като наблюдатели за ефекти от различните действия с удостоверенията и устройствата
   */
  ngOnInit() {
    this.devicesService.initAllDevices();

    this.store
      .select(selectorDevice)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(devices => {
        if (devices instanceof Array) {
          this.dialog.closeAll();

          // Clonning devices, because we are modifying the array
          this._devices = JSON.parse(JSON.stringify(devices));
          // Check if devices array is empty, to show "No devices found" message
          if (this._devices.length === 0) {
            this.setDeviceData(true, []);
          } else {
            // If there there is a selected certificate in that case we are in component in current certificate context
            if (this._selectedCert) {
              // Extracts only the devices in current certificate
              const filteredDevices = devices.filter(device => {
                return device.certificateReqIds.find(element => element === this._selectedCert.certReqId);
              });
              this.setDeviceData(filteredDevices.length === 0, filteredDevices);
            } else {
              // Sets all devices as data source for devices grid
              this.setDeviceData(false, devices);
            }
          }
        }
      });

    this.info.dismissAll();

    this.effects.operationFailed$
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe(
        () => {
          this.logger.log('operationFailed effect subscritpion');
          this.operationResult(this.translateService.instant(this.resultTexts + 'operationFailed'));
        }
      );

    this.isEmergencyLogin = this.emergency.getEmergencyState();
  }

  setDeviceData(isEmpty: boolean, devices: IMobileDevice[]) {
    this.onEmptyDevices = isEmpty;
    this.setDataSource(devices);
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
    this.dataSource.filter = filterValue;
    this.dataSource.filterPredicate = this.tableHelper.filterPredicateForDevices;
  }

  showCertificates(device: IMobileDevice) {
    this.onShowCertificates.emit(device);
  }

  deleteDevice(device: IMobileDevice) {
    this.openConfirmDialog(
      'deleteAllTitle',
      'deleteAllQuestion',
      () => this.store.dispatch(new ActionDevicesDelete({ deviceId: device.appId })),
      device
    );
  }

  removeDeviceFromCertificate(device: IMobileDevice) {
    this.openConfirmDialog(
      'removeFromCertTitle',
      'removeFromCertQuestion',
      () => this.store.dispatch(new ActionRemoveDeviceFromCertificatе({ deviceId: device.appId, certReqId: this._selectedCert.certReqId })),
      device
    );

  }

  isValid(status: DeviceStatus) {
    return status === DeviceStatus.ACTIVE;
  }

  isHolded(status: DeviceStatus) {
    return status === DeviceStatus.BLOCKED;
  }

  statusIcon(status: DeviceStatus): IStatusIcon {
    return statusIcon[status];
  }

  /**
   * Set the paginator and sort after the view init since this component will
   * be able to query its view for the initialized paginator and sort.
   */
  ngAfterViewInit() {
    this.setPaginator();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setDataSource(devices: IMobileDevice[]) {
    this.loading = false;
    this.dataSource.data = devices;
    this.setPaginator();
  }

  private setPaginator() {
    if (this.dataSource) {
      this.dataSource!.sort = this.sort;
      this.dataSource!.paginator = this.paginator;
    }
  }

  private openConfirmDialog(
    title: string,
    question: string,
    // tslint:disable-next-line:ban-types
    confirmAction: Function,
    device: IMobileDevice
  ) {
    this._confirmDialog = this.dialog.open(
      ConfirmComponent,
      {
        data: {
          title: this.translateService.instant(this.dialogTexts + title),
          question: this.translateService.instant(this.dialogTexts + question, { device: device.name }),
          confirmTxt: this.translateService.instant('common.yes'),
          cancelTxt: this.translateService.instant('common.no'),
          confirmAction
        } as IConfirmData
      });
  }

  private operationResult(msg: string, isSuccessful?: boolean) {
    if (this._confirmDialog) {
      this._confirmDialog.close();
    }
    isSuccessful ? this.info.success(msg) : this.info.error(msg);
  }

}
